/**
 * The Continuum mark.
 *
 * Two shapes, one idea: a pulse flowing into an infinity loop. `Mark` is the
 * square icon for the header and anything small; `MarkLockup` is the wide
 * version with the pulse, for the login page.
 *
 * Both inherit `currentColor` and carry no fill, so one component serves light
 * mode, dark mode and print. No gradient, deliberately — a gradient would not
 * inherit the text colour and would muddy in one-colour print.
 *
 * The favicon is a separate file (`public/favicon.svg`) with the colour baked
 * in, because a favicon has no CSS context for `currentColor` to resolve
 * against; it carries its own `prefers-color-scheme` rule instead.
 *
 * Both are `aria-hidden`: the mark is decorative and the name is always set as
 * text beside it. Without this a screen reader announces "Continuum" twice.
 *
 * Geometry is a Gerono figure-eight with the crossing interlaced. The interlace
 * is not decoration — a Bernoulli lemniscate's shallow crossing fills into a
 * solid blob at every stroke weight this interface needs, which is why the mark
 * survives 16 px and the obvious construction does not. Do not "tidy" the gap
 * away: it was measured at 1.4, 1.8, 2.2 and 2.4 stroke weights before the curve
 * was swapped. The renders that showed it are not in the repo; the conclusion is
 * the part worth keeping.
 *
 * Not blue, also deliberately. Cosmic, TakeCare and Melior are blue, and a
 * clinician has to tell the reserve system from the real one at a glance — the
 * same requirement the permanent banner in App.tsx exists to serve.
 */

type MarkProps = {
  /** Rendered height in pixels. */
  size?: number
}

export function Mark({ size = 24 }: MarkProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.9"
      strokeLinecap="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M2.5 12C2.59 12.57 2.69 14.51 3.04 15.39C3.39 16.27 3.94 17.04 4.61 17.28C5.27 17.52 6.12 17.33 7.01 16.82C7.91 16.32 8.96 15.26 9.99 14.23C11.02 13.2 12.15 11.75 13.2 10.65C14.24 9.56 15.32 8.34 16.27 7.67C17.21 6.99 18.12 6.56 18.85 6.6C19.58 6.65 20.21 7.19 20.65 7.93C21.09 8.68 21.37 9.95 21.46 11.07C21.56 12.18 21.46 13.62 21.2 14.61C20.94 15.6 20.47 16.57 19.88 17C19.3 17.43 18.51 17.5 17.67 17.17C16.82 16.85 15.28 15.4 14.81 15.05" />
      <path d="M9.19 8.95C9.05 8.82 8.61 8.39 8.32 8.14C8.04 7.9 7.75 7.67 7.48 7.48C7.21 7.29 6.95 7.12 6.69 6.99C6.43 6.86 6.18 6.76 5.95 6.7C5.71 6.63 5.48 6.6 5.26 6.6C5.04 6.6 4.83 6.64 4.64 6.71C4.44 6.78 4.26 6.89 4.09 7.02C3.92 7.16 3.76 7.33 3.61 7.52C3.47 7.72 3.33 7.95 3.22 8.19C3.1 8.44 2.99 8.72 2.91 9.01C2.82 9.3 2.74 9.62 2.68 9.94C2.62 10.27 2.58 10.61 2.55 10.95C2.52 11.29 2.51 11.83 2.5 12" />
    </svg>
  )
}

/**
 * The wide lockup. Below roughly 30 px high the pulse loses its shape — use
 * `Mark` instead rather than scaling this one down.
 */
export function MarkLockup({ size = 66 }: MarkProps) {
  return (
    <svg
      width={(size * 46) / 24}
      height={size}
      viewBox="0 0 46 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M1.8 12L5.6 12L8.4 4.6L11.4 19.8L14 12L17.6 12" />
      <path d="M19.6 12C19.71 12.63 19.82 14.8 20.23 15.78C20.64 16.76 21.29 17.61 22.06 17.87C22.83 18.13 23.82 17.91 24.87 17.34C25.92 16.76 27.14 15.57 28.34 14.41C29.54 13.26 30.85 11.63 32.07 10.41C33.28 9.2 34.54 7.85 35.63 7.12C36.72 6.39 37.77 5.93 38.61 6.01C39.45 6.09 40.18 6.73 40.67 7.58C41.16 8.43 41.48 9.88 41.57 11.13C41.66 12.37 41.53 13.98 41.21 15.06C40.88 16.15 40.32 17.19 39.62 17.63C38.92 18.07 38 18.09 37 17.68C36 17.27 34.2 15.6 33.64 15.19" />
      <path d="M27.56 8.81C27.39 8.66 26.86 8.16 26.53 7.87C26.19 7.59 25.86 7.32 25.53 7.09C25.21 6.87 24.9 6.67 24.59 6.51C24.29 6.35 23.99 6.22 23.71 6.14C23.42 6.06 23.15 6.01 22.89 6C22.63 5.99 22.38 6.03 22.15 6.1C21.92 6.17 21.7 6.28 21.5 6.43C21.29 6.57 21.1 6.76 20.93 6.97C20.75 7.19 20.6 7.44 20.46 7.72C20.32 8 20.19 8.31 20.08 8.63C19.98 8.96 19.89 9.32 19.82 9.68C19.74 10.04 19.69 10.43 19.65 10.82C19.62 11.2 19.61 11.8 19.6 12" />
    </svg>
  )
}
